/**
 * Team/community hook — bridges Concord V2 communities and simple teams.
 *
 * Primary mode: Concord V2 communities loaded from the user's kind-13302 list.
 * Fallback: Simple localStorage teams when Concord data is unavailable.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNostrUser } from "./useNostr";
import { useLiveCommunities2, useCommunity2 } from "@/concord-v2/hooks/useCommunityList2";
import type { CommunityV2 } from "@/concord-v2/lib/types";

/** Unified team view */
export interface TeamInfo {
  id: string;
  name: string;
  memberCount: number;
  memberPubkeys: string[];
  isOwner: boolean;
  source: "simple" | "concord";
  /** The underlying Concord community (when source === "concord") */
  community?: CommunityV2;
}

// ── Simple team fallback (localStorage) ────────────────────────────────

const STORAGE_KEY = "onegroup:team";

interface SimpleTeam {
  id: string;
  name: string;
  memberPubkeys: string[];
  createdAt: number;
}

function loadSimpleTeam(): SimpleTeam | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return null;
}

function saveSimpleTeam(team: SimpleTeam) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(team)); } catch { /* ignore */ }
}

function generateTeamId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ── Primary hook ───────────────────────────────────────────────────────

export function useTeam() {
  const { user } = useNostrUser();
  const entries = useLiveCommunities2(); // Concord V2 entries

  const [simpleTeam, setSimpleTeam] = useState<SimpleTeam | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load simple team from storage (for non-Concord users)
  useEffect(() => {
    setIsLoading(true);
    const stored = loadSimpleTeam();
    if (stored && user && stored.memberPubkeys.includes(user.pubkey)) {
      setSimpleTeam(stored);
    }
    setIsLoading(false);
  }, [user?.pubkey]);

  // Build team list: Concord first, then simple fallback
  const allTeams = useMemo<TeamInfo[]>(() => {
    const teams: TeamInfo[] = [];

    // Concord communities as teams
    for (const entry of entries) {
      if (!user) continue;
      teams.push({
        id: entry.community_id,
        name: entry.current.name || entry.community_id.slice(0, 8),
        memberCount: 0, // Will be populated from guestbook
        memberPubkeys: [user.pubkey],
        isOwner: entry.current.owner === user.pubkey,
        source: "concord",
      });
    }

    // Simple team fallback
    if (simpleTeam && user) {
      // Only show if not already covered by a Concord team
      if (!teams.some((t) => t.id === simpleTeam.id)) {
        teams.push({
          id: simpleTeam.id,
          name: simpleTeam.name,
          memberCount: simpleTeam.memberPubkeys.length,
          memberPubkeys: simpleTeam.memberPubkeys,
          isOwner: simpleTeam.memberPubkeys[0] === user.pubkey,
          source: "simple",
        });
      }
    }

    return teams;
  }, [entries, simpleTeam, user]);

  // First team is the active one
  const team = allTeams[0] ?? null;

  const createTeam = useCallback((name: string) => {
    if (!user) return null;
    const newTeam: SimpleTeam = {
      id: generateTeamId(),
      name,
      memberPubkeys: [user.pubkey],
      createdAt: Math.floor(Date.now() / 1000),
    };
    saveSimpleTeam(newTeam);
    setSimpleTeam(newTeam);
    return newTeam;
  }, [user]);

  const addMember = useCallback((pubkey: string) => {
    setSimpleTeam((prev) => {
      if (!prev) return prev;
      if (prev.memberPubkeys.includes(pubkey)) return prev;
      const updated = { ...prev, memberPubkeys: [...prev.memberPubkeys, pubkey] };
      saveSimpleTeam(updated);
      return updated;
    });
  }, []);

  return {
    team,
    allTeams,
    isLoading,
    createTeam,
    addMember,
    hasTeam: !!team,
    concordCount: entries.length,
  };
}

/**
 * Get a specific Concord community by ID.
 */
export function useConcordTeam(communityIdHex: string | undefined): CommunityV2 | undefined {
  return useCommunity2(communityIdHex);
}